import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { PLATFORM_DATA_EVENT_NAME, waitForPlatformDataEvent } from '@/app/common/tracking/platformDataUtils.ts';

/**
 * Page-wide bootstrap of BIFF, the internal ads client (`window.brg.ui.biff`, biff-ui 8.x).
 *
 * The script is injected as early as possible and independently of the cookie banner: biff itself holds
 * `load()` and `installBanners()` back until GTM signals consent through `window.LMN_COOKIES_GO` (flag or
 * window event of the same name), so we never wait for `cookieGO` here. Everything talks to biff through the
 * `brg.ui.biff.cmd` command queue, which works both before and after the script has arrived.
 *
 * Integration guide: content-hub-pink/packages/biff-ui/INTEGRATION.md
 */

export const BIFF_SCRIPT_ID = 'biff-loader';
export const BIFF_SCRIPT_VERSION = '8';
export const BIFF_API_VERSION = 'v4';
export const LMN_COOKIES_GO_EVENT = 'LMN_COOKIES_GO';

const SLOW_CONNECTION_TYPES = ['slow-2g', '2g'];

export const getBiffDomain = (isDev: boolean) => (isDev ? 'https://qa-cdn.biff.travel' : 'https://cdn.biff.travel');

export const getBiffSrc = (isDev: boolean) => `${getBiffDomain(isDev)}/scripts/${BIFF_SCRIPT_VERSION}/biff.min.js`;

/** No trailing slash: biff builds `${endpoint}/${platform}/${brand}/content` itself. */
export const getBiffEndpoint = (isDev: boolean) => `${getBiffDomain(isDev)}/api/${BIFF_API_VERSION}`;

/** Same contract as `prepareBiffQueue` from @lmn/contenthub-utilities, inlined because that package is only a transitive dependency. */
export const prepareBiffQueue = (): BiffQueue => {
  if (!window.brg) window.brg = {};
  const { brg } = window;
  if (!brg.ui) brg.ui = {};
  const { ui } = brg;
  if (!ui.biff) ui.biff = {};
  const { biff } = ui;
  if (!biff.cmd) biff.cmd = [];
  return biff.cmd;
};

/** Runs `command` once biff is ready (immediately if it already is). */
export const biffCmd = (command: BiffCommand): void => {
  if (!canUseDOM) return;
  prepareBiffQueue().push(command);
};

export const hasBiffScript = (): boolean => canUseDOM && !!document.getElementById(BIFF_SCRIPT_ID);

/**
 * True on a slow connection (2g / slow-2g) or with data-saver enabled. Devices without the Network Information
 * API count as fast. Only the hotel detail page skips its ads on this, as before biff 8; the search page never did.
 */
export const hasSlowConnection = (): boolean => {
  if (!canUseDOM) return false;

  const connection = navigator?.connection;
  if (connection?.saveData) return true;
  return !!connection?.effectiveType && SLOW_CONNECTION_TYPES.includes(connection.effectiveType);
};

/**
 * Runs `command` once gpt.js is loaded, at once if it already is: the googletag command queue, which biff
 * loads gpt.js into as well. Needed for anything touching `pubads()`: biff's `load()` resolves before gpt.js
 * has arrived, so a direct call right after it would find no `pubads` and silently do nothing.
 */
const gptCmd = (command: () => void): void => {
  if (!canUseDOM) return;
  window.googletag = window.googletag || { cmd: [] };
  window.googletag.cmd = window.googletag.cmd || [];
  window.googletag.cmd.push(command);
};

/** The configuration biff last completed `load()` for. */
let loadedKey: string | undefined;

/**
 * The configuration that was loaded when gpt first requested each slot, i.e. the configuration that defined it.
 * Keyed by slot object, not id: gpt keeps a page's slots after biff loads the next configuration, and the
 * results and hotel detail configurations define slots under the same ids (`div-gpt-ad-intrip1`).
 */
let requestedSlotKeys = new WeakMap<BiffGoogletagSlot, string>();
let slotRequestTrackingAttached = false;

/** Records every slot gpt requests, so `refreshRemountedSlot` can tell a served slot from one still waiting for its first display. */
export const attachSlotRequestTracking = (): void => {
  if (slotRequestTrackingAttached || !canUseDOM) return;
  slotRequestTrackingAttached = true;

  gptCmd(() => {
    window.googletag?.pubads?.().addEventListener('slotRequested', (event) => {
      // Only the first request counts: biff refreshes the slots of earlier configurations along with the new ones.
      if (loadedKey && !requestedSlotKeys.has(event.slot)) requestedSlotKeys.set(event.slot, loadedKey);
    });
  });
};

/** The googletag slots defined for `slotId`, in the order gpt holds them. */
const getGptSlots = (slotId: string): BiffGoogletagSlot[] => {
  if (!canUseDOM) return [];
  return (
    window.googletag
      ?.pubads?.()
      .getSlots?.()
      .filter((slot) => slot.getSlotElementId() === slotId) || []
  );
};

/** The googletag slot defined for `slotId`, if the configurations biff loaded list it. */
export const getGptSlot = (slotId: string): BiffGoogletagSlot | undefined => getGptSlots(slotId)[0];

/** Key-values OpenWrap sets on a slot for the bid that won biff's auction in `load()`. */
const OPENWRAP_TARGETING_PREFIX = 'pwt';

/**
 * Requests a new ad for a slot gpt already served. The OpenWrap bid key-values stay on the slot after its
 * ad rendered, and a plain refresh would send that used bid again. They are cleared first, so the request
 * goes to the ad server without a bid. Biff runs the auction only in `load()`, and it sends the slots right after
 * it sets the bid, so any bid found on a slot here was already used.
 */
export const refreshServedSlot = (slot: BiffGoogletagSlot): void => {
  const usedBid = Object.keys(slot.getConfig('targeting').targeting || {})
    .filter((key) => key.startsWith(OPENWRAP_TARGETING_PREFIX))
    .reduce<Record<string, null>>((keys, key) => ({ ...keys, [key]: null }), {});
  if (Object.keys(usedBid).length > 0) slot.setConfig({ targeting: usedBid });
  window.googletag?.pubads?.().refresh([slot]);
};

/**
 * Brings an ad gpt already served for configuration `key` back into the node that now carries `slotId`,
 * after the node it rendered into was replaced (the results list is remounted on a mobile list/map switch, and
 * again on the way back from a hotel detail page). `installBanners` cannot do this: biff only calls
 * `display`, which gpt ignores after the first time. Only a slot first requested while `key` was loaded is
 * refreshed: another page's slot under the same id would put its ad unit into this page. A slot gpt has not
 * requested yet is left alone: its pending display finds the current node by id on its own, and a refresh on
 * top would be a second request. Returns whether a refresh was issued.
 */
export const refreshRemountedSlot = (slotId: string, key: string): boolean => {
  // The newest one: a configuration loaded again (results, hotel detail, results) defines its slots again.
  const slot = getGptSlots(slotId)
    .filter((candidate) => requestedSlotKeys.get(candidate) === key)
    .pop();
  if (!slot) return false;

  window.googletag?.display?.(slotId);
  refreshServedSlot(slot);
  return true;
};

type BiffBootParams = {
  /** QA or local: QA CDN + QA API endpoint. */
  isDev: boolean;
  /** Local dev only: GTM is not loaded there, so nothing would ever grant consent to biff. */
  isLocal: boolean;
};

/** Injects the biff script once per page. Safe to call repeatedly. */
export const bootBiff = ({ isDev, isLocal }: BiffBootParams): void => {
  if (!canUseDOM || hasBiffScript()) return;

  // Both must be in place before the tag is appended: biff reads the flag at init time.
  if (isLocal) window.LMN_COOKIES_GO = true;
  window.addEventListener(
    LMN_COOKIES_GO_EVENT,
    () => {
      // Should GTM dispatch the event before biff has initialised, keep it as the flag biff reads at init.
      window.LMN_COOKIES_GO = true;
    },
    { once: true, passive: true }
  );

  // First command in the queue, so it precedes any load().
  if (isDev) biffCmd((biff) => biff.helpers.setEndpoint(getBiffEndpoint(true)));

  const script = document.createElement('script');
  script.id = BIFF_SCRIPT_ID;
  script.src = getBiffSrc(isDev);
  script.async = true;
  document.head.appendChild(script);
};

type LoadBiffParams = {
  isLoggedIn: boolean;
  /** Identifies the page configuration; `load()` runs again only when it changes (e.g. search → hotel detail). */
  key: string;
  /** `window.platformData.length` when the page mounted, so only this page's visit event is awaited. */
  fromIndex?: number;
  signal?: AbortSignal;
};

type KeyedLoad = {
  /** Resolves once `load()` for this key has completed; a failed or aborted attempt leaves it pending for a retry. */
  loaded: Promise<void>;
  resolveLoaded: () => void;
  /** The attempt in flight or completed; cleared again when it resolved `false`, so the next `loadBiff` retries. */
  request?: Promise<boolean>;
  /** Whether `request` has settled. */
  settled: boolean;
};

const createKeyedLoad = (): KeyedLoad => {
  let resolveLoaded: KeyedLoad['resolveLoaded'] = () => {};
  const loaded = new Promise<void>((resolve) => {
    resolveLoaded = resolve;
  });
  return { loaded, resolveLoaded, settled: false };
};

/** One entry per configuration requested or awaited since biff last loaded a different one. */
const loads = new Map<string, KeyedLoad>();

const getKeyedLoad = (key: string): KeyedLoad => {
  const existing = loads.get(key);
  if (existing) return existing;

  const keyedLoad = createKeyedLoad();
  loads.set(key, keyedLoad);
  return keyedLoad;
};

type QueuedLoad = {
  key: string;
  /** Settles the attempt as `false` without waiting for biff. */
  giveUp: () => void;
};

/** The `load()` most recently handed to biff and not settled yet. */
let queuedLoad: QueuedLoad | undefined;

/**
 * Resolves once biff has finished `load()` for the given page configuration, i.e. after consent, the
 * configuration fetch and gpt.js. Anything that reaches into gpt or registers slots for that configuration has
 * to wait for this: see `loadBiff` for why slot registration cannot be queued early. Keyed on purpose: a
 * waiter for the detail page must not be released by a slow results-page `load()` finishing after a
 * client-side navigation. Safe to call before `loadBiff` for the key (child effects run before parent effects).
 */
export const whenBiffLoaded = (key: string): Promise<void> => getKeyedLoad(key).loaded;

/** True while a `loadBiff` for this key is in flight or has completed since biff last loaded another configuration. */
export const isBiffLoadRequested = (key: string): boolean => !!loads.get(key)?.request;

/**
 * True while biff holds the configuration `load()` fetched for this key. Anything biff installs from now on
 * (AFS included, with the search query of that configuration) goes into this configuration.
 */
export const isBiffLoaded = (key: string): boolean => loadedKey === key;

const runLoad = ({ isLoggedIn, key, signal }: LoadBiffParams): Promise<boolean> =>
  new Promise((resolve) => {
    const attempt: QueuedLoad = { key, giveUp: () => finish(false) };

    function finish(loaded: boolean) {
      if (queuedLoad === attempt) queuedLoad = undefined;
      signal?.removeEventListener('abort', attempt.giveUp);
      resolve(loaded);
    }

    if (signal?.aborted) {
      finish(false);
      return;
    }

    // A load() for another configuration supersedes the one before it, whatever consent is: before consent
    // biff 8 keeps a single resolver and the earlier load() is orphaned, its promise never settling; after
    // consent biff runs both, and the earlier one completing last would put its configuration — and its
    // slots — back over the newer page's. Settle the earlier attempt here either way, so it installs nothing
    // and leaves its key free for a retry (a hotel detail page opened, left for the results, opened again).
    queuedLoad?.giveUp();
    queuedLoad = attempt;
    // The page left before biff answered (HotelDetailBiff unmount): nothing will use the answer.
    signal?.addEventListener('abort', attempt.giveUp, { once: true });

    biffCmd((biff) => {
      // Queued commands run when biff arrives and again are held until consent, so an attempt given up in
      // the meantime gets here long after it settled: skipping load() keeps it from replacing the slots of
      // whichever configuration the page has moved on to.
      if (signal?.aborted || queuedLoad !== attempt) return;

      biff
        .withAuctions()
        .load({ isLoggedIn })
        .then(() => {
          // Set even when the attempt was given up meanwhile: biff has defined this configuration's slots anyway.
          loadedKey = key;
          attachSlotRequestTracking();
          finish(true);
        })
        .catch((error) => {
          console.error('[biff] load failed', { key, error });
          finish(false);
        });
    });
  });

const requestLoad = async (params: LoadBiffParams): Promise<boolean> => {
  const visitTracked = await waitForPlatformDataEvent({
    eventName: PLATFORM_DATA_EVENT_NAME.VISIT,
    fromIndex: params.fromIndex ?? 0,
    signal: params.signal,
  });

  // Best effort, not a precondition: a page that never pushes a visit event (analyticsResultsEvent stays
  // silent without a destination parameter) would otherwise get no ads at all, where before biff 8 it got
  // ads without page context. Load anyway and let biff fall back. An aborted wait is not this case —
  // runLoad sees the same signal and settles false without calling biff.
  if (!visitTracked && !params.signal?.aborted) {
    console.warn('[biff] no visit event in platformData, loading without page context', { key: params.key });
  }

  return runLoad(params);
};

/**
 * Asks biff for the page configuration once the page's `visit` event is in `platformData` (biff builds the
 * request from `page.*` and `transactionAffiliation` there), or once waiting for it has timed out. Biff
 * queues the call until consent.
 *
 * Resolves `true` once that configuration's slots are defined, `false` when the page was left (`signal`), the
 * load was superseded by another configuration, or it failed. Register the page's slots only after it resolves
 * `true`: biff runs `installBanners` at once while the previous configuration's slots are still the defined
 * ones, and drops every id it does not know. Repeated calls for the same key share one promise; a `false`
 * outcome lets a later call try again, and so does a completed `load()` for another key, since that replaces
 * this key's slots (e.g. RESULTS after a client-side results → hotel detail → new search round trip).
 */
export const loadBiff = (params: LoadBiffParams): Promise<boolean> => {
  if (!canUseDOM) return Promise.resolve(false);

  const keyedLoad = getKeyedLoad(params.key);
  if (keyedLoad.request) return keyedLoad.request;

  const request = requestLoad(params)
    .catch((error) => {
      console.error('[biff] load failed', { key: params.key, error });
      return false;
    })
    .then((loaded) => {
      keyedLoad.settled = true;
      if (loaded) {
        keyedLoad.resolveLoaded();
        // biff now holds this configuration and the others' slots are gone: forget the keys it has already
        // answered, so their next request runs load() again. An attempt still in flight cleans up after
        // itself when it settles, and a key only waited on (whenBiffLoaded before loadBiff) keeps its
        // waiters — dropping it would leave them on a promise nothing resolves any more.
        loads.forEach((other, otherKey) => {
          if (other !== keyedLoad && other.request && other.settled) loads.delete(otherKey);
        });
      } else if (keyedLoad.request === request) {
        keyedLoad.request = undefined;
        keyedLoad.settled = false;
      }
      return loaded;
    });
  keyedLoad.request = request;

  return request;
};

export const installBanners = (params: BiffInstallBannersParams): void => {
  biffCmd((biff) => biff.Includer.installBanners(params));
};

export const resetBiffLoaderForTests = (): void => {
  loads.clear();
  queuedLoad = undefined;
  loadedKey = undefined;
  slotRequestTrackingAttached = false;
  requestedSlotKeys = new WeakMap();
};
